import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StatusBadgeWithTooltip from '@/components/StatusBadgeWithTooltip';
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  Clock,
  XCircle,
  Users,
  LayoutGrid,
  Archive,
  ArchiveRestore,
  RotateCcw,
  Pencil,
  Ban,
} from 'lucide-react';
import PractitionerOverviewTab from '@/components/practitioner-detail/PractitionerOverviewTab';
import { Practitioner } from '@/lib/types';
import { practitionerService } from '@/services/practitionerService';
import { useClinic } from '@/contexts/ClinicContext';
import PractitionerPatients from '@/components/practitioner-detail/PractitionerPatients';
import ArchivePractitionerDialog from '@/components/dialog/ArchivePractitionerDialog';
import ResubmitPractitionerSheet from '@/components/clinic-detail/ResubmitPractitionerSheet';
import EditPractitionerSheet from '@/components/clinic-detail/EditPractitionerSheet';
import { permissionCheck } from '@/lib/permission-check';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb';

const PractitionerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { clinic, isAdmin, rolePermissions } = useClinic();
  const navigate = useNavigate();
  const { setLabel: setBreadcrumbLabel } = useBreadcrumbLabel();
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [loading, setLoading] = useState(true);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isResubmitSheetOpen, setIsResubmitSheetOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  const fetchPractitioner = async () => {
    try {
      setLoading(true);
      const practitionerId = id ?? '';
      const response = await practitionerService.getPractitionerById(clinic!.id, practitionerId);
      setPractitioner(response.data?.practitioner);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    fetchPractitioner();
  }, [id]);

  useEffect(() => {
    setBreadcrumbLabel(practitioner?.full_name ?? null);
    return () => setBreadcrumbLabel(null);
  }, [practitioner?.full_name, setBreadcrumbLabel]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };


  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header with back button */}
        <div className='flex items-start gap-4'>
          <Skeleton className='h-9 w-9 rounded-lg' />
          <div className='space-y-2'>
            <Skeleton className='h-7 w-48' />
            <Skeleton className='h-4 w-64' />
          </div>
        </div>

        {/* Header Card */}
        <div className='rounded-lg border border-slate-200 bg-white p-5'>
          <div className='flex flex-col gap-5 sm:flex-row sm:items-center'>
            <Skeleton className='h-16 w-16 rounded-xl' />
            <div className='flex-1 space-y-3'>
              <div className='flex items-center gap-3'>
                <Skeleton className='h-6 w-40' />
                <Skeleton className='h-5 w-16 rounded-full' />
              </div>
              <div className='flex gap-4'>
                <Skeleton className='h-4 w-28' />
                <Skeleton className='h-4 w-40' />
              </div>
            </div>
            <div
              className='flex items-center gap-3 rounded-lg px-4 py-3'
              style={{ backgroundColor: 'rgb(245, 247, 247)' }}
            >
              <Skeleton className='h-5 w-5' />
              <div className='space-y-1'>
                <Skeleton className='h-3 w-12' />
                <Skeleton className='h-6 w-8' />
              </div>
            </div>
            <Skeleton className='h-9 w-28 rounded-lg' />
          </div>
        </div>

        {/* Tabs */}
        <div className='space-y-6'>
          <div
            className='flex gap-2 rounded-lg border border-slate-200 p-1'
            style={{ backgroundColor: 'rgb(245, 247, 247)' }}
          >
            <Skeleton className='h-9 w-24 rounded-md' />
            <Skeleton className='h-9 w-24 rounded-md' />
          </div>

          {/* Content area */}
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='space-y-4 rounded-lg border border-slate-200 bg-white p-5'>
              <Skeleton className='h-5 w-32' />
              <div className='space-y-3'>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className='flex justify-between'>
                    <Skeleton className='h-4 w-20' />
                    <Skeleton className='h-4 w-32' />
                  </div>
                ))}
              </div>
            </div>
            <div className='space-y-4 rounded-lg border border-slate-200 bg-white p-5'>
              <Skeleton className='h-5 w-28' />
              <div className='space-y-3'>
                {[1, 2, 3].map(i => (
                  <div key={i} className='flex justify-between'>
                    <Skeleton className='h-4 w-24' />
                    <Skeleton className='h-4 w-28' />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!practitioner) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='text-center'>
          <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100'>
            <XCircle className='h-6 w-6 text-slate-400' />
          </div>
          <p className='mb-4 text-sm text-slate-600'>Practitioner not found</p>
          <Button variant='outline' size='sm' onClick={() => navigate('/')} className='text-sm'>
            <ArrowLeft className='mr-2 h-4 w-4' />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const showArchiveButton =
    practitioner.is_active &&
    practitioner.status === 'active' &&
    permissionCheck('practitioners:update', rolePermissions, isAdmin);

  const showRestoreButton =
    !practitioner.is_active &&
    practitioner.status === 'active' &&
    permissionCheck('practitioners:update', rolePermissions, isAdmin);

  const showResubmitButton =
    practitioner.status === 'rejected' &&
    permissionCheck('practitioners:update', rolePermissions, isAdmin);

  const showEditButton =
    (practitioner.status === 'active' || practitioner.status === 'archive') &&
    permissionCheck('practitioners:update', rolePermissions, isAdmin);

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex items-start gap-4'>
        <Button
          variant='outline'
          size='icon'
          onClick={handleBack}
          className='h-9 w-9 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          style={{ marginTop: '4px' }}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h1 className='font-montserrat text-xl font-bold text-slate-900 sm:text-2xl'>
            Practitioner Details
          </h1>
          <p className='text-sm text-slate-500'>View and manage practitioner information</p>
        </div>
      </div>

      {/* Header Card */}
      <div className='rounded-lg border border-slate-200 bg-white p-5'>
        <div className='flex flex-col gap-5 sm:flex-row sm:items-center'>
          {/* Avatar */}
          <div className='flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-teal-100'>
            <span className='text-xl font-semibold text-teal-700'>
              {getInitials(practitioner.full_name)}
            </span>
          </div>

          {/* Info */}
          <div className='min-w-0 flex-1'>
            <div className='mb-2 flex flex-wrap items-center gap-3'>
              <h1 className='text-xl font-semibold text-slate-900'>{practitioner.full_name}</h1>
              <StatusBadgeWithTooltip
                status={practitioner.status}
                userStatus={practitioner.user?.status}
                isActive={practitioner.is_active}
              />
            </div>

            <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500'>
              {practitioner.npi && (
                <span>
                  <span className='text-slate-400'>NPI:</span>{' '}
                  <span className='font-mono font-medium text-slate-700'>{practitioner.npi}</span>
                </span>
              )}
              {practitioner.email && <span className='text-slate-600'>{practitioner.email}</span>}
            </div>
          </div>

          {/* Stats */}
          {practitioner.patient_count !== undefined && (
            <div
              className='flex items-center gap-3 rounded-lg px-4 py-3'
              style={{ backgroundColor: 'rgb(245, 247, 247)' }}
            >
              <Users className='h-5 w-5 text-slate-400' />
              <div>
                <p className='text-xs font-medium uppercase tracking-wide text-slate-500'>
                  Patients
                </p>
                <p className='text-lg font-semibold text-slate-900'>{practitioner.patient_count}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex items-center gap-3'>
            {showEditButton && (
              <>
                {practitioner.status !== 'pending' && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-2 border-brand-teal text-brand-teal hover:border-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal'
                    onClick={() => setIsEditSheetOpen(true)}
                  >
                    <Pencil className='h-4 w-4' />
                    Edit
                  </Button>
                )}
              </>
            )}

            {showArchiveButton && (
              <Button
                variant='outline'
                size='sm'
                className='gap-2 border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700'
                onClick={() => setIsArchiveDialogOpen(true)}
              >
                <Archive className='h-4 w-4' />
                Archive
              </Button>
            )}

            {showRestoreButton && (
              <Button
                variant='outline'
                size='sm'
                className='gap-2 border-brand-teal text-brand-teal hover:border-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal'
                onClick={() => setIsArchiveDialogOpen(true)}
              >
                <ArchiveRestore className='h-4 w-4' />
                Restore
              </Button>
            )}

            {showResubmitButton && (
              <Button
                variant='outline'
                size='sm'
                className='gap-2 border-brand-teal text-brand-teal hover:border-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal'
                onClick={() => setIsResubmitSheetOpen(true)}
              >
                <RotateCcw className='h-4 w-4' />
                Resubmit Application
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue='overview' className='space-y-6'>
        <TabsList
          className='h-auto w-full justify-start gap-1 rounded-lg border border-slate-200 p-1'
          style={{ backgroundColor: 'rgb(245, 247, 247)' }}
        >
          <TabsTrigger
            value='overview'
            className='rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
          >
            <LayoutGrid className='mr-2 h-4 w-4' />
            Overview
          </TabsTrigger>
          <TabsTrigger
            value='patients'
            className='rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-all data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm'
          >
            <Users className='mr-2 h-4 w-4' />
            Patients
          </TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='mt-6 focus-visible:outline-none'>
          <PractitionerOverviewTab practitioner={practitioner} refetch={fetchPractitioner} />
        </TabsContent>

        <TabsContent value='patients' className='mt-6 focus-visible:outline-none'>
          <PractitionerPatients practitionerId={practitioner.id} clinicId={clinic?.id} />
        </TabsContent>
      </Tabs>

      {/* Archive/Activate Dialog */}
      {isArchiveDialogOpen && (
        <ArchivePractitionerDialog
          open={isArchiveDialogOpen}
          onOpenChange={setIsArchiveDialogOpen}
          practitioner={practitioner}
          onSuccess={async () => {
            await fetchPractitioner();
            setIsArchiveDialogOpen(false);
          }}
        />
      )}

      {/* Resubmit Practitioner Sheet */}
      {isResubmitSheetOpen && clinic && (
        <ResubmitPractitionerSheet
          open={isResubmitSheetOpen}
          onOpenChange={setIsResubmitSheetOpen}
          clinicId={clinic.id}
          practitioner={practitioner}
          onSuccess={async () => {
            await fetchPractitioner();
            setIsResubmitSheetOpen(false);
          }}
        />
      )}

      {/* Edit Practitioner Sheet */}
      {isEditSheetOpen && clinic && practitioner && (
        <EditPractitionerSheet
          open={isEditSheetOpen}
          onOpenChange={setIsEditSheetOpen}
          practitioner={practitioner}
          clinicId={clinic.id}
          onPractitionerUpdated={async () => {
            await fetchPractitioner();
            setIsEditSheetOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default PractitionerDetail;
